import { useEffect, useRef, useState } from 'react';
import { IoMdClose } from 'react-icons/io';
import { AiOutlinePlus, AiOutlineMinus } from 'react-icons/ai';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { getIcon } from 'utils/icons';

const ProductViewModal = (props: {
    isOpen: any;
    onClose: any;
    product: any;
    newOrder: any;
    onAddProduct: any;
    onQuantityChange: any;
    hidePricesOnShop?: boolean;
    showProductDetailsOnShop?: boolean;
    selectedPackaging?: any;
    onPackagingChange?: (packaging: any) => void;
}) => {
    const {
        isOpen,
        onClose,
        product,
        newOrder,
        onAddProduct,
        onQuantityChange,
        hidePricesOnShop = false,
        showProductDetailsOnShop = false,
        selectedPackaging,
        onPackagingChange,
    } = props;

    const [quantity, setQuantity] = useState<number | null>(null);
    const [newQuantity, setNewQuantity] = useState(1);
    const [selectedChild, setSelectedChild] = useState<any>(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const touchStartXRef = useRef<number | null>(null);

    const hasChildren = product?.children && product.children.length > 0;

    // If some packagings are b2bOnly, restrict display to those and hide the base unit
    const rawPackagings = product?.packagings || [];
    const b2bOnlyPackagings = rawPackagings.filter((p: any) => p.b2bOnly);
    const effectivePackagings = b2bOnlyPackagings.length > 0 ? b2bOnlyPackagings : rawPackagings;
    const hideBaseUnit = b2bOnlyPackagings.length > 0;

    useEffect(() => {
        if (!product) return;

        setSelectedChild(null);
        
        const productOrderInfo = newOrder.items.find(
            (i: any) => i.productId === product.id
        );

        if (productOrderInfo) {
            setNewQuantity(productOrderInfo.quantity);
            setQuantity(productOrderInfo.quantity);
        } else {
            setQuantity(null);
            setNewQuantity(1);
        }
    }, [product, newOrder]);

    const handleChildSelect = (childId: string) => {
        const child = product.children.find((c: any) => c.id === childId);
        if (!child) return;

        setSelectedChild(child);

        const existingItem = newOrder.items.find(
            (i: any) => i.productId === childId
        );

        if (existingItem) {
            setQuantity(existingItem.quantity);
            setNewQuantity(existingItem.quantity);
        } else {
            setQuantity(null);
            setNewQuantity(1);
        }
    };

    const handleParentSelect = () => {
        setSelectedChild(null);

        const productOrderInfo = newOrder.items.find(
            (i: any) => i.productId === product.id
        );

        if (productOrderInfo) {
            setQuantity(productOrderInfo.quantity);
            setNewQuantity(productOrderInfo.quantity);
        } else {
            setQuantity(null);
            setNewQuantity(1);
        }
    };

    const handleSubmit = () => {
        const targetId = selectedChild ? selectedChild.id : product.id;
        const targetName = selectedChild
            ? selectedChild.name
            : product.name;

        if (!quantity) {
            onAddProduct(targetId, targetName, newQuantity);
        } else {
            const currentItemId = selectedChild ? selectedChild.id : product.id;
            onQuantityChange(newQuantity || 0, currentItemId);
            if (newQuantity === 0) setNewQuantity(1);
        }
        onClose();
    };

    // When b2bOnly packagings exist and nothing is explicitly selected yet, use default (or first) for price/unity display
    const autoB2bPackaging = hideBaseUnit && !selectedPackaging
        ? (b2bOnlyPackagings.find((p: any) => p.isDefault) || b2bOnlyPackagings[0])
        : null;
    const resolvedPackaging = selectedPackaging || autoB2bPackaging;

    const rawPriceToShow = selectedChild ? selectedChild.price : (resolvedPackaging?.price || product?.price);
    const priceToShow = resolvedPackaging?.useReprocessedPrice && resolvedPackaging?.weight > 0
        ? rawPriceToShow * resolvedPackaging.weight
        : rawPriceToShow;
    const unityToShow = selectedChild ? selectedChild.unity : (resolvedPackaging?.unity || product?.unity);

    const subUnitQuantityToShow = selectedChild ? selectedChild.subUnitQuantity : product?.subUnitQuantity;
    const subUnityToShow = selectedChild ? selectedChild.subUnity : product?.subUnity;
    const showSubUnitPrice = !resolvedPackaging && subUnitQuantityToShow > 0 && !!subUnityToShow;

    // All images for the item currently shown (parent or selected child)
    const getAllImages = (item: any): string[] => {
        if (Array.isArray(item?.imageUrls) && item.imageUrls.length > 0) {
            return item.imageUrls;
        }
        if (item?.imageUrl) {
            return [item.imageUrl];
        }
        return [];
    };

    const allImages = selectedChild ? getAllImages(selectedChild) : getAllImages(product);

    // Reset the carousel whenever the shown item changes
    useEffect(() => {
        setCurrentImageIndex(0);
    }, [product, selectedChild]);

    const goToPreviousImage = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        setCurrentImageIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
    };

    const goToNextImage = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        setCurrentImageIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));
    };

    const handleImageTouchStart = (e: React.TouchEvent) => {
        touchStartXRef.current = e.touches[0].clientX;
    };

    const handleImageTouchEnd = (e: React.TouchEvent) => {
        if (touchStartXRef.current === null || allImages.length <= 1) return;
        const deltaX = e.changedTouches[0].clientX - touchStartXRef.current;
        touchStartXRef.current = null;
        if (Math.abs(deltaX) < 40) return;
        if (deltaX > 0) goToPreviousImage();
        else goToNextImage();
    };

    const descriptionToShow = selectedChild?.description || product?.description;

    // PVC - Prix de vente conseillé, shown as-is when filled on the product fiche
    const recommendedPriceToShow = selectedChild
        ? selectedChild.recommendedPrice
        : product?.recommendedPrice;

    // Fields shown in the detail modal, in order, only when filled in —
    // opt-in: nothing shows unless the shop owner enabled showProductDetailsOnShop
    const detailFields = showProductDetailsOnShop
        ? [
              { label: 'Variété', value: product?.variety },
              { label: 'Spécificité', value: product?.specificity },
              { label: 'Producteur', value: product?.producer },
              { label: 'Calibre', value: product?.calibre },
              { label: 'Origine', value: product?.origin },
              { label: 'Libellé agriculture', value: product?.agricultureLabel },
          ].filter((f) => f.value)
        : [];

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[1001] flex items-center justify-center p-4">
            {/* Overlay */}
            <div 
                className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative z-[1002] w-full max-w-xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl">
                {/* Header with image */}
                <div
                    className="relative h-44 bg-gradient-to-br from-blue-50 to-slate-100 overflow-hidden"
                    onTouchStart={handleImageTouchStart}
                    onTouchEnd={handleImageTouchEnd}
                >
                    <button
                        onClick={onClose}
                        className="absolute top-3 right-3 z-20 p-1.5 bg-white/90 backdrop-blur-sm hover:bg-white rounded-full shadow-lg transition-all hover:scale-110"
                    >
                        <IoMdClose size={20} className="text-slate-700" />
                    </button>

                    {/* Product image */}
                    <div className="absolute inset-0 flex items-center justify-center p-4">
                        {allImages.length > 0 ? (
                            <img
                                src={allImages[currentImageIndex]}
                                className="max-h-32 max-w-full object-contain drop-shadow-2xl"
                                alt={selectedChild?.name || product?.name}
                                onError={(e: any) => {
                                    e.target.src = getIcon(product?.name, 'product');
                                }}
                            />
                        ) : (
                            <img
                                className="max-h-32 max-w-full object-contain drop-shadow-2xl"
                                src={getIcon(product?.name, 'product')}
                                alt={product?.name}
                            />
                        )}
                    </div>

                    {/* Carousel navigation - only when the product has more than one photo */}
                    {allImages.length > 1 && (
                        <>
                            <button
                                onClick={goToPreviousImage}
                                className="absolute left-2 top-1/2 -translate-y-1/2 z-20 p-1.5 bg-black/40 hover:bg-black/60 text-white rounded-full transition-colors"
                            >
                                <MdChevronLeft size={18} />
                            </button>
                            <button
                                onClick={goToNextImage}
                                className="absolute right-2 top-1/2 -translate-y-1/2 z-20 p-1.5 bg-black/40 hover:bg-black/60 text-white rounded-full transition-colors"
                            >
                                <MdChevronRight size={18} />
                            </button>
                            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-20 flex gap-1.5">
                                {allImages.map((_, index) => (
                                    <button
                                        key={index}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setCurrentImageIndex(index);
                                        }}
                                        className={`h-1.5 w-1.5 rounded-full transition-colors ${
                                            index === currentImageIndex ? 'bg-blue-600' : 'bg-white/70'
                                        }`}
                                    />
                                ))}
                            </div>
                        </>
                    )}

                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent pointer-events-none" />
                </div>

                {/* Content */}
                <div className="px-4 pb-4 -mt-6 relative z-10">
                    {/* Title and price */}
                    <div className="bg-white rounded-xl shadow-lg p-3 mb-4">
                        <h1 className="text-lg font-bold text-slate-800 mb-1">{product?.name}</h1>
                        {!hasChildren && !hidePricesOnShop && (
                            <div className="flex items-baseline gap-2 flex-wrap">
                                <span className="text-2xl font-bold text-blue-600">
                                    {priceToShow?.toFixed(2)}€
                                </span>
                                <span className="text-sm text-slate-500">/ {unityToShow}</span>
                                {showSubUnitPrice && (
                                    <span className="text-sm text-slate-500">
                                        ({(priceToShow / subUnitQuantityToShow).toFixed(2)}€ / {subUnityToShow})
                                    </span>
                                )}
                            </div>
                        )}
                        {!hasChildren && !hidePricesOnShop && recommendedPriceToShow > 0 && (
                            <p className="mt-1 text-xs text-slate-500">
                                PVC conseillé : <span className="font-semibold text-slate-700">{recommendedPriceToShow.toFixed(2)}€</span>
                            </p>
                        )}
                        
                        {/* Packaging selector for products without children */}
                        {!hasChildren && product?.packagings && product.packagings.length > 0 && (
                            <div className="mt-3">
                                <label className="block text-xs font-semibold text-slate-700 mb-2">
                                    Conditionnement
                                </label>
                                <select
                                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                                    value={selectedPackaging?.unity || ''}
                                    onChange={(e) => {
                                        if (e.target.value === '') {
                                            onPackagingChange?.(null);
                                        } else {
                                            const packaging = effectivePackagings.find(
                                                (p: any) => p.unity === e.target.value
                                            );
                                            onPackagingChange?.(packaging);
                                        }
                                    }}
                                >
                                    {!hideBaseUnit && (
                                        <option value="">
                                            Unité de base ({product.unity})
                                        </option>
                                    )}
                                    {effectivePackagings.map((pkg: any, idx: number) => (
                                        <option key={idx} value={pkg.unity}>
                                            {pkg.unity} ({pkg.weight} {product.unity}) - {pkg.price}€
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>

                    {/* Description */}
                    {descriptionToShow && (
                        <div className="bg-white rounded-xl shadow-lg p-3 mb-4">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">
                                Description
                            </p>
                            <p className="text-sm text-slate-700 whitespace-pre-line">
                                {descriptionToShow}
                            </p>
                        </div>
                    )}

                    {/* Product details */}
                    {detailFields.length > 0 && (
                        <div className="bg-white rounded-xl shadow-lg p-3 mb-4">
                            <div className="grid grid-cols-2 gap-2">
                                {detailFields.map((f) => (
                                    <div
                                        key={f.label}
                                        className="rounded-lg bg-slate-50 border border-slate-100 px-2.5 py-2 min-w-0"
                                    >
                                        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                                            {f.label}
                                        </p>
                                        <p
                                            className="text-sm font-semibold text-slate-800 truncate mt-0.5"
                                            title={f.value}
                                        >
                                            {f.value}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Packaging selector */}
                    {hasChildren && (
                        <div className="mb-4">
                            <label className="block text-xs font-semibold text-slate-700 mb-2">
                                Choisir le conditionnement
                            </label>
                            <div className="space-y-2">
                                {/* Option parent */}
                                <button
                                    onClick={handleParentSelect}
                                    className={`w-full flex items-center justify-between rounded-xl border-2 p-3 transition-all ${
                                        !selectedChild
                                            ? 'border-blue-600 bg-blue-50 shadow-md scale-[1.02]'
                                            : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
                                    }`}
                                >
                                    <div className="text-left flex-1 min-w-0">
                                        <p className={`font-semibold text-sm truncate ${!selectedChild ? 'text-blue-900' : 'text-slate-800'}`}>
                                            {product?.name}
                                        </p>
                                        <p className="text-xs text-slate-600 mt-0.5">
                                            {product?.unity}
                                            {product?.subUnity && product?.subUnitQuantity > 0 && (
                                                <span className="ml-1">
                                                    ({product.subUnitQuantity} {product.subUnity})
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                    {!hidePricesOnShop && (
                                        <div className="text-right ml-2">
                                            <p className={`text-lg font-bold ${!selectedChild ? 'text-blue-600' : 'text-slate-800'}`}>
                                                {product?.price?.toFixed(2)}€
                                            </p>
                                            {product?.recommendedPrice > 0 && (
                                                <p className="text-[10px] text-slate-400">
                                                    PVC {product.recommendedPrice.toFixed(2)}€
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </button>

                                {/* Options children */}
                                {product?.children
                                    ?.filter((child: any) => child.price > 0)
                                    .map((child: any) => (
                                        <button
                                            key={child.id}
                                            onClick={() => handleChildSelect(child.id)}
                                            className={`w-full flex items-center justify-between rounded-xl border-2 p-3 transition-all ${
                                                selectedChild?.id === child.id
                                                    ? 'border-blue-600 bg-blue-50 shadow-md scale-[1.02]'
                                                    : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
                                            }`}
                                        >
                                            <div className="text-left flex-1 min-w-0">
                                                <p className={`font-semibold text-sm truncate ${selectedChild?.id === child.id ? 'text-blue-900' : 'text-slate-800'}`}>
                                                    {child.name}
                                                </p>
                                                <p className="text-xs text-slate-600 mt-0.5">
                                                    {child.unity}
                                                    {child.subUnity && child.subUnitQuantity > 0 && (
                                                        <span className="ml-1">
                                                            ({child.subUnitQuantity} {child.subUnity})
                                                        </span>
                                                    )}
                                                </p>
                                            </div>
                                            {!hidePricesOnShop && (
                                                <div className="text-right ml-2">
                                                    <p className={`text-lg font-bold ${selectedChild?.id === child.id ? 'text-blue-600' : 'text-slate-800'}`}>
                                                        {child.price.toFixed(2)}€
                                                    </p>
                                                    {child.recommendedPrice > 0 && (
                                                        <p className="text-[10px] text-slate-400">
                                                            PVC {child.recommendedPrice.toFixed(2)}€
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        </button>
                                    ))}
                            </div>
                        </div>
                    )}

                    {/* Quantity */}
                    <div className="bg-slate-50 rounded-xl p-3 mb-4">
                        <label className="block text-xs font-semibold text-slate-700 mb-2">
                            Quantité
                        </label>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => setNewQuantity(Math.max(0, newQuantity - 1))}
                                className="w-10 h-10 flex items-center justify-center bg-white hover:bg-slate-100 rounded-lg border-2 border-slate-200 transition-all hover:scale-105"
                            >
                                <AiOutlineMinus size={16} className="text-slate-700" />
                            </button>

                            <div className="flex-1 relative">
                                <input
                                    value={newQuantity.toString()}
                                    min={0}
                                    onChange={(e) =>
                                        setNewQuantity(e.target.value ? parseFloat(e.target.value) : 0)
                                    }
                                    type="number"
                                    className="w-full h-10 text-center text-lg font-bold rounded-lg border-2 border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">
                                    {unityToShow}
                                </span>
                            </div>

                            <button
                                onClick={() => setNewQuantity(newQuantity + 1)}
                                className="w-10 h-10 flex items-center justify-center bg-blue-600 hover:bg-blue-700 rounded-lg transition-all hover:scale-105 shadow-lg"
                            >
                                <AiOutlinePlus size={16} className="text-white" />
                            </button>
                        </div>

                        {/* total price  */}
                        {!hidePricesOnShop && (
                            <div className="mt-3 flex items-center justify-between p-2.5 bg-white rounded-lg border-2 border-blue-100">
                                <span className="text-sm text-slate-700 font-semibold">Total HT</span>
                                <span className="text-xl font-bold text-blue-600">
                                    {(newQuantity * priceToShow).toFixed(2)} €
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="flex gap-2">
                        {quantity && (
                            <button
                                onClick={onClose}
                                className="flex-1 py-2.5 rounded-lg border-2 border-slate-200 text-sm text-slate-700 font-semibold hover:bg-slate-50 transition-all"
                            >
                                Annuler
                            </button>
                        )}
                        <button
                            onClick={() => {
                                if ((!quantity && newQuantity > 0) || quantity) handleSubmit();
                            }}
                            disabled={!quantity && newQuantity === 0}
                            className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                                !newQuantity || newQuantity === 0
                                    ? 'bg-red-500 hover:bg-red-600 text-white shadow-lg hover:shadow-xl'
                                    : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl'
                            } ${(!quantity && newQuantity === 0) ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {!quantity ? 'Ajouter au panier' : newQuantity > 0 ? 'Modifier la quantité' : 'Supprimer du panier'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductViewModal;